import { ReactLocalization } from "@fluent/react"

import { toDineroOrUndefined } from "../../../../modules/currency/currencyLib"
import { MoneyFactory } from "../../../../modules/currency/useMoney"

import { ExternalServiceEntry, ExtraServicePricingType } from "./extraServicesTypes"

/** Stable MRT row id so extra-service rows keep identity across reorders. */
export const getExtraServiceRowId = (row: ExternalServiceEntry): string => String(row.id)

type ExtraServicePriceSublines = Readonly<{
    subtitle1: string
    subtitle2: string | null
    subtitle3: string | null
}>

/**
 * Build the price sublines shown under an extra service's name.
 * Pure: derives only from the entry plus the localization/money formatters.
 */
export function getExtraServicePriceSublines(
    entry: ExternalServiceEntry,
    l10n: ReactLocalization,
    moneyFactory: MoneyFactory,
): ExtraServicePriceSublines {
    if (entry.pricingType === ExtraServicePricingType.FLAT_FEE) {
        const amount = toDineroOrUndefined(entry.price)

        return {
            subtitle1: amount
                ? `${l10n.getString("extra-services-price-subline-fixed")}: ${moneyFactory.format(amount)}`
                : "",
            subtitle2: null,
            subtitle3: null,
        }
    }

    const perSeat = toDineroOrUndefined(entry.pricePerSeat)
    const min = toDineroOrUndefined(entry.minimumPrice)
    const max = toDineroOrUndefined(entry.maximumPrice)

    return {
        subtitle1: perSeat
            ? `${l10n.getString("extra-services-price-subline-per-seat")}: ${moneyFactory.format(perSeat)}`
            : "",
        subtitle2: min ? `${l10n.getString("extra-services-price-subline-min")}: ${moneyFactory.format(min)}` : null,
        subtitle3: max ? `${l10n.getString("extra-services-price-subline-max")}: ${moneyFactory.format(max)}` : null,
    }
}

/** Move the dragged extra service to the target's position (pure, for optimistic reorder). */
export function reorderExtraServices(
    services: ExternalServiceEntry[],
    draggingItem: ExternalServiceEntry,
    targetItem: ExternalServiceEntry,
): ExternalServiceEntry[] {
    const fromIndex = services.findIndex((service) => service.id === draggingItem.id)
    const toIndex = services.findIndex((service) => service.id === targetItem.id)

    if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
        return services
    }

    const next = [...services]
    const [moved] = next.splice(fromIndex, 1)
    next.splice(toIndex, 0, moved)

    return next
}
